import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import panic from 'errors/panic';
import { useApi } from 'api/api-context';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { pickBy } from 'lodash';
import { RevisionFormData } from 'components/forms/revision/fill-out/types';
import { IForm } from 'pages/revisions-module/template-editor/editors/form/types';
import { normalizeIndexValue } from 'components/forms/revision/fill-out/data/normalize-index-value';
import { useDisclosure } from '@mantine/hooks';
import { FEATURE_TOGGLE_REVISION_AUTO_SAVE_DRAFT } from 'env';
import sleep from 'utils/sleep';
import { REVISION_NOT_IN_PROGRESS_ERROR_CODE } from 'utils/constants';
import { useRevisionLockProvider } from 'components/forms/revision/fill-out/data/RevisionLockProvider';

interface SaveDraftParams {
  notify?: boolean;
  overrideData?: RevisionFormData;
  throwOnError?: boolean;
}

interface IFillOutRevisionSaveDraftProviderContext {
  saveDraft: (options?: SaveDraftParams) => Promise<void>;
  loading: boolean;
  autoSaving: boolean;
  lastSavedTimestamp: number;
}

const FillOutRevisionSaveDraftProviderContext = createContext<IFillOutRevisionSaveDraftProviderContext>(undefined!);
const AUTO_SAVE_INTERVAL_MS = 10000;

/**
 * Provides the revision and form spec to the children.
 */
export function FillOutRevisionSaveDraftProvider({ children }: { children: ReactNode }) {
  const { revision, form, changesAcknowledged, dataInitialized } = useFillOutRevisionDataProvider();
  const { getAction } = useApi();
  const { lockToken, registerTakeoverSaveHandler, stage } = useRevisionLockProvider();
  const [loading, setLoading] = useState(false);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inFlightSaveDraftPromiseRef = useRef<Promise<void> | null>(null);
  const inFlightSaveDraftTokenRef = useRef<symbol | null>(null);
  const formVersionRef = useRef(0);
  const lastPersistedVersionRef = useRef(0);
  const hasSeenFormValuesRef = useRef(false);

  const formSpec = useMemo(() => JSON.parse(revision.revisionTemplate!.formScheme!) as IForm, [revision]);

  const indexedInputs = useMemo(
    () =>
      formSpec.sections
        .flatMap(({ rows }) => rows)
        .flatMap(({ inputs }) => inputs)
        .filter(({ spec, context }) => spec.index && context === 'revision'),
    [formSpec]
  );

  const [lastSavedTimestamp, setLastSavedTimestamp] = useState<number>(-1);
  const [autoSaving, { open: startAutoSaving, close: stopAutoSaving }] = useDisclosure(false);

  /**
   * Starts a new draft save for the latest known form snapshot.
   * Parallel callers should await the shared in-flight promise instead.
   */
  const startSaveDraft = useCallback(
    async ({ notify = true, overrideData, throwOnError = false }: SaveDraftParams = {}) => {
      const saveVersion = formVersionRef.current;
      const data = overrideData || form.values;
      const saveToken = Symbol('saveDraft');
      const keptDevices = pickBy(data.devices, ({ removed }) => !removed);
      const indexValues = indexedInputs.flatMap(({ spec }) => [
        { fieldName: spec.name, value: normalizeIndexValue(spec, data.fields[spec.name]) },
      ]);

      const savePromise = (async () => {
        setLoading(true);

        try {
          const saveDraft = getAction('RevisionSaveDraft');

          await Promise.all([
            sleep(1000), // Artificial delay to prevent flickering
            saveDraft({
              parameters: { revisionId: String(revision.revisionId) },
              payload: {
                lockToken,
                revisionData: JSON.stringify({ ...data, devices: keptDevices }),
                indexValues,
              },
            }),
          ]);

          lastPersistedVersionRef.current = Math.max(lastPersistedVersionRef.current, saveVersion);

          if (notify) {
            setLastSavedTimestamp(Date.now());
          }
        } catch (error: any) {
          const code = error?.response?.data?.error?.code;

          if (code === REVISION_NOT_IN_PROGRESS_ERROR_CODE && !throwOnError) {
            // Do nothing, this happens if the user tries to save a draft for a revision that has been submitted in another tab (or by another user), so the "in progress" status is lost. The user will see an error when they try to submit, but there's no need to also show an error on auto-save.
          } else {
            panic(error);
          }

          if (throwOnError) {
            throw error;
          }
        } finally {
          if (inFlightSaveDraftTokenRef.current === saveToken) {
            inFlightSaveDraftTokenRef.current = null;
            inFlightSaveDraftPromiseRef.current = null;
          }

          setLoading(false);
        }
      })();

      inFlightSaveDraftTokenRef.current = saveToken;
      inFlightSaveDraftPromiseRef.current = savePromise;

      return savePromise;
    },
    [form.values, getAction, indexedInputs, lockToken, revision.revisionId]
  );

  /** Saves the revision data as a draft (without validation). */
  const saveDraft = useCallback(
    (options: SaveDraftParams = {}) => {
      if (inFlightSaveDraftPromiseRef.current) {
        return inFlightSaveDraftPromiseRef.current;
      }

      return startSaveDraft(options);
    },
    [startSaveDraft]
  );

  const saveDraftRef = useRef(saveDraft);

  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  useEffect(() => {
    if (!hasSeenFormValuesRef.current) {
      hasSeenFormValuesRef.current = true;
      return;
    }

    formVersionRef.current += 1;
  }, [form.values]);

  /**
   * Stops the periodic auto-save loop.
   */
  const stopAutoSaveInterval = useCallback(() => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }
  }, []);

  /**
   * Starts the periodic auto-save loop with at most one active timer.
   */
  const startAutoSaveInterval = useCallback(() => {
    stopAutoSaveInterval();

    autoSaveIntervalRef.current = setInterval(() => {
      if (inFlightSaveDraftPromiseRef.current) {
        return;
      }

      startAutoSaving();
      saveDraftRef
        .current({ notify: false })
        .catch(panic)
        .finally(() => {
          stopAutoSaving();
        });
    }, AUTO_SAVE_INTERVAL_MS);
  }, [startAutoSaving, stopAutoSaveInterval, stopAutoSaving]);

  useEffect(() => {
    if (FEATURE_TOGGLE_REVISION_AUTO_SAVE_DRAFT && changesAcknowledged && dataInitialized && stage === 'ready') {
      startAutoSaveInterval();
    } else {
      stopAutoSaveInterval();
    }

    return () => stopAutoSaveInterval();
  }, [changesAcknowledged, dataInitialized, stage, startAutoSaveInterval, stopAutoSaveInterval]);

  useEffect(() => {
    registerTakeoverSaveHandler(async () => {
      while (true) {
        if (inFlightSaveDraftPromiseRef.current) {
          await inFlightSaveDraftPromiseRef.current;
        }

        if (lastPersistedVersionRef.current >= formVersionRef.current) {
          return;
        }

        await startSaveDraft({ notify: false, throwOnError: true });
      }
    });

    return () => registerTakeoverSaveHandler(null);
  }, [registerTakeoverSaveHandler, startSaveDraft]);

  const value = useMemo(
    () => ({ saveDraft, loading, autoSaving, lastSavedTimestamp }),
    [saveDraft, loading, autoSaving, lastSavedTimestamp]
  );

  return (
    <FillOutRevisionSaveDraftProviderContext.Provider value={value}>
      {children}
    </FillOutRevisionSaveDraftProviderContext.Provider>
  );
}

/**
 * Hook to get the revision and form spec.
 */
export function useFillOutRevisionSaveDraftProvider() {
  return useContext(FillOutRevisionSaveDraftProviderContext);
}
